import { useCallback, useEffect, useState } from "react";
import type { FavoriteTarget } from "@oshimap/domain";
import { FavoritesStore } from "@oshimap/query";
import { ep, session } from "../../lib/api";

/**
 * 저장(하트) 훅.
 *
 * 규칙은 `@oshimap/query` 의 `FavoritesStore` 가 압니다 — 낙관적 갱신, 실패
 * 롤백, 게스트가 눌렀을 때 로그인 뒤 이어서 하기, 중복 요청 합치기. Native 도
 * 같은 store 위에 자기 훅을 씁니다. 여기는 **구독과 오류 문구만** 합니다.
 *
 * 상태를 한 벌로 두는 이유는 전에 화면마다 사본이 있어서 같은 장소의 하트가
 * 화면마다 달랐기 때문입니다. 자세한 재현은 store 의 주석에 있습니다.
 */
const store = new FavoritesStore(ep, session);

/** 테스트에서 모듈 상태를 초기화한다. 화면 코드는 쓰지 않는다. */
export function resetFavoritesForTest(): void {
  store.reset();
}

export function useFavorites() {
  const [current, setCurrent] = useState<ReadonlySet<string>>(store.keys);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const off = store.subscribe(setCurrent);
    setCurrent(store.keys);          // 구독 사이에 바뀌었을 수 있다
    return off;
  }, []);

  const isFavorited = useCallback(
    (t: FavoriteTarget, id: string) => current.has(`${t}:${id}`),
    [current],
  );

  const toggle = useCallback(
    async (t: FavoriteTarget, id: string, onNeedAuth?: () => void): Promise<boolean> => {
      setError(null);
      try {
        return await store.toggle(t, id, onNeedAuth);
      } catch (e) {
        setError(e instanceof Error ? e.message : "저장하지 못했어요. 다시 시도해 주세요.");
        return store.has(t, id);
      }
    },
    [],
  );

  return { isFavorited, toggle, error, reload: () => store.load() };
}
